import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type {
  DemandBoard,
  DemandDraft,
  DemandSnapshot,
  DraftInput,
  Placement,
  PlacementKind,
} from '../types/taDemand';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-520260363689.europe-west3.run.app';

const base = (period: string) => `${PROXY_URL}/api/ta-demand/${encodeURIComponent(period)}`;

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.message || body?.detail || body?.error || '';
    } catch {
      /* a non-JSON error body tells us nothing useful */
    }
    throw new ApiError(detail || `Request failed (${res.status})`, res.status);
  }
  return res.json() as Promise<T>;
}

/** Carries the status so callers can tell "not configured" from "broke". */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function fetchDemandBoard(period: string): Promise<DemandBoard> {
  return authenticatedFetch(base(period)).then((r) => json<DemandBoard>(r));
}

export function saveDemandDraft(
  period: string,
  id: string,
  input: DraftInput,
): Promise<{ draft: DemandDraft }> {
  return authenticatedFetch(`${base(period)}/drafts/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  }).then((r) => json<{ draft: DemandDraft }>(r));
}

export function deleteDemandDraft(
  period: string,
  id: string,
  department: string,
): Promise<{ ok: true }> {
  const q = new URLSearchParams({ department });
  return authenticatedFetch(
    `${base(period)}/drafts/${encodeURIComponent(id)}?${q}`,
    { method: 'DELETE' },
  ).then((r) => json<{ ok: true }>(r));
}

/**
 * Approve one draft. A 501 means the write token is not configured and a 409
 * means somebody already approved it — both are messages for the user, not
 * crashes, so the caller checks `status` rather than only the text.
 */
export function approveDemandDraft(
  period: string,
  id: string,
): Promise<{ ok: true; recordId: string; warning?: string }> {
  return authenticatedFetch(`${base(period)}/drafts/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
  }).then((r) => json<{ ok: true; recordId: string; warning?: string }>(r));
}

export function addDemandTeam(
  period: string,
  department: string,
  unit: string,
  team: string,
): Promise<{ team: string }> {
  return authenticatedFetch(`${base(period)}/teams`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ department, unit, team }),
  }).then((r) => json<{ team: string }>(r));
}

export function submitDemandUnit(period: string, department: string, unit: string) {
  return authenticatedFetch(`${base(period)}/units/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ department, unit }),
  }).then((r) => json<{ unit: { submittedBy: string; submittedAt: string } }>(r));
}

export function submitDemandSection(period: string, department: string) {
  return authenticatedFetch(
    `${base(period)}/sections/${encodeURIComponent(department)}/submit`,
    { method: 'POST' },
  ).then((r) => json<{ section: { submittedBy: string; submittedAt: string } }>(r));
}

/**
 * Move one role into a team.
 *
 * A blank `team` is a real value — "no team yet" — and CLEARS a team the
 * job-family map assigned. Without that a lead could not undo a wrong map entry,
 * only move the role somewhere else wrong.
 */
export function moveDemandRoleToTeam(
  period: string,
  roleId: string,
  team: string,
  department: string,
): Promise<{ moved: { roleId: string; team: string } | null }> {
  return authenticatedFetch(`${base(period)}/roles/${encodeURIComponent(roleId)}/team`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ team, department }),
  }).then((r) => json<{ moved: { roleId: string; team: string } | null }>(r));
}

/**
 * Freeze the Hiring Database as it stands right now.
 *
 * Airtable exposes no field-level revision history, so this is the only way a
 * trimester ever gets a plan to be measured against. A 502 means the HDB could
 * not be read — the route refuses to store an empty baseline, which would read
 * as a trimester nobody planned.
 */
export function freezeDemandBaseline(
  period: string,
  body: { label?: string; note?: string; kind?: 'baseline' | 'snapshot' } = {},
): Promise<{ snapshot: DemandSnapshot }> {
  return authenticatedFetch(`${base(period)}/snapshots`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => json<{ snapshot: DemandSnapshot }>(r));
}

/**
 * Freeze an Airtable CSV EXPORT as a baseline.
 *
 * The file is parsed and shaped on the SERVER, by the same code the live freeze
 * uses — a baseline has to be comparable against the board, and a browser-side
 * shaping would be a second answer to what a role is. `takenAt` is the date the
 * export DESCRIBES, not the day it was uploaded: stamping today would misdate
 * every comparison made against it.
 */
export function importDemandBaseline(
  period: string,
  body: {
    csv: string;
    id?: string;
    label?: string;
    note?: string;
    takenAt?: string;
    kind?: 'baseline' | 'snapshot';
  },
): Promise<{ snapshot: DemandSnapshot; unmapped: string[] }> {
  return authenticatedFetch(`${base(period)}/snapshots/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => json<{ snapshot: DemandSnapshot; unmapped: string[] }>(r));
}

/**
 * Restructure: move a role, a person, or a whole team.
 *
 * One call for all three — it is one question, what moved and where to. A field
 * left out of `to` means unchanged, and an EMPTY `to` removes the move, which is
 * how a restructure is undone: the row goes back to what the HDB or the people
 * database says rather than to another guess of ours.
 *
 * A team has no id anywhere, so it is addressed by where it WAS
 * (`department`/`unit`/`team`) — the only stable handle it has.
 */
export function placeDemandRow(
  period: string,
  body: {
    kind: PlacementKind;
    id?: string;
    department?: string;
    unit?: string;
    team?: string;
    to: Placement;
    section?: string;
  },
): Promise<{ moved: { key: string } | null }> {
  return authenticatedFetch(`${base(period)}/placements`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => json<{ moved: { key: string } | null }>(r));
}

/**
 * Settle one hire↔colleague question.
 *
 * THREE ANSWERS, and the difference between the last two is the whole point.
 * A person id links them; `null` says keep them separate — a real answer that
 * has to be remembered, or the board asks about a departed hire on every load;
 * and OMITTING `personId` clears the decision so matching decides again, which
 * is how a mistaken merge is undone.
 *
 * An overlay: nothing reaches the Hiring Database or the people database.
 */
export function linkDemandHire(
  period: string,
  roleId: string,
  personId: string | null | undefined,
): Promise<{ link: { roleId: string } }> {
  return authenticatedFetch(`${base(period)}/hire-links/${encodeURIComponent(roleId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // `undefined` must not become `"personId": null` — JSON.stringify drops an
    // undefined value, which is exactly the "no field at all" the route reads
    // as "clear this decision".
    body: JSON.stringify(personId === undefined ? {} : { personId }),
  }).then((r) => json<{ link: { roleId: string } }>(r));
}
